/**
 * The one place the app asks "has the user asked for less motion?" (Conversation feed, SA-03).
 *
 * `behavior: 'smooth'` on `scrollTo` / `scrollIntoView` ignores the operating-system "reduce motion"
 * setting, so every programmatic scroll in the Conversation area (and in the shared Tiptap editor, which
 * the Conversation hosts) takes its behaviour from `scrollBehavior()` instead of writing the literal:
 *
 *     el.scrollTo({ top, behavior: ReducedMotionHelper.scrollBehavior() });
 *
 * `'auto'` is the instant jump (no CSS in the app sets `scroll-behavior`, so `'auto'` never turns smooth).
 * The CSS side of Reduce Motion lives in the components' own `@media (prefers-reduced-motion: reduce)`
 * blocks; the same query string is used here so the two can never disagree.
 *
 * Static and DI-free, like `ToolbarTooltipHelper` next to it, so a pure class or a spec can call it.
 */

/** Anything that can answer a media query: `window`, or a stub in a spec. */
export interface MotionPreferenceSource {
    matchMedia?: (query: string) => { matches: boolean };
}

export class ReducedMotionHelper {

    /** The media query behind both the helper and the components' CSS reduced-motion blocks. */
    static readonly REDUCE_QUERY = '(prefers-reduced-motion: reduce)';

    /**
     * True when the user asked for reduced motion. False when the answer is unknown: no `matchMedia`
     * (old browser, server render, a stub without it) or one that throws. A wrong guess here only costs a
     * smooth scroll, so the failure mode is the app's pre-existing behaviour.
     */
    static prefersReducedMotion(
        source: MotionPreferenceSource | null | undefined = typeof window === 'undefined' ? null : window
    ): boolean {
        try {
            if (!source || typeof source.matchMedia !== 'function') {
                return false;
            }
            const result = source.matchMedia(ReducedMotionHelper.REDUCE_QUERY);
            return !!result && result.matches === true;
        } catch {
            return false;
        }
    }

    /** `'auto'` (instant) when the user asked for reduced motion, otherwise `'smooth'`. */
    static scrollBehavior(
        source: MotionPreferenceSource | null | undefined = typeof window === 'undefined' ? null : window
    ): ScrollBehavior {
        return ReducedMotionHelper.prefersReducedMotion(source) ? 'auto' : 'smooth';
    }
}
